<script lang="ts">
import type { ModalProps } from '#ui/types';
import type { ButtonProps } from './Button.vue';

// 15vw = 100 - 85vw, keeping the margin equal horizontally and vertically.
export const WIDE_MODAL_CONTENT_CLASS = 'block-[max(100dvh-15vw,100dvh-var(--spacebar-width)*2)] inline-[min(max(85vw,100vw-var(--spacebar-width)*2),1440px)] max-inline-none';

const SIZE_CONTENT_CLASS = {
	sm: 'sm:max-w-md',
	md: 'sm:max-w-lg',
	lg: 'sm:max-w-2xl',
	xl: 'sm:max-w-4xl',
	wide: WIDE_MODAL_CONTENT_CLASS,
} as const;

export type DialogSize = keyof typeof SIZE_CONTENT_CLASS;
export const DIALOG_SIZES = Object.keys(SIZE_CONTENT_CLASS) as DialogSize[];

export interface DialogProps {
	title?: string;
	description?: string;
	size?: DialogSize;
	confirm?: boolean | Partial<ButtonProps>;
	cancel?: boolean | Partial<ButtonProps>;
	close?: boolean | Partial<ButtonProps>;
	dismissible?: boolean;
	ui?: ModalProps['ui'];
}

export interface DialogEmits {
	close: [confirm: boolean];
}

export interface DialogSlots {
	default: () => any;
	title: () => any;
	description: () => any;
	body: (props: { close: () => void }) => any;
	footer: (props: { close: () => void }) => any;
	confirm: () => any;
	cancel: () => any;
}
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { omit } from '#layers/base/public';

const {
	size = 'md',
	cancel = true,
	close = { size: 'sm' },
	dismissible = true,
	confirm,
	ui,
} = defineProps<DialogProps>();
const emit = defineEmits<DialogEmits>();
const slots = defineSlots<DialogSlots>();

const { t } = useI18n();
const ownSlotNames = ['confirm', 'cancel', 'footer'] as const;

const hasFooter = computed(() =>
	confirm !== false
	|| cancel !== false
	|| !!slots.footer
	|| !!slots.confirm
	|| !!slots.cancel,
);

const confirmProps = computed(() => ({
	variant: 'solid' as const,
	color: 'primary' as const,
	size: 'sm' as const,
	label: t('common.confirm'),
	onClick: () => emit('close', true),
	...(typeof confirm === 'object' ? confirm : {}),
}));

const cancelProps = computed(() => ({
	variant: 'outline' as const,
	color: 'neutral' as const,
	size: 'sm' as const,
	label: t('common.cancel'),
	onClick: () => emit('close', false),
	...(typeof cancel === 'object' ? cancel : {}),
}));

const computedUi = computed<ModalProps['ui']>(() => ({
	content: SIZE_CONTENT_CLASS[size],
	...ui,
}));
</script>

<template>
	<DModal
		:title
		:description
		:close
		:dismissible
		:ui="computedUi"
	>
		<template v-for="(_, name) in omit(slots, ownSlotNames)" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>

		<template v-if="hasFooter" #footer="footerData">
			<slot name="footer" v-bind="footerData">
				<slot name="cancel">
					<DButton v-if="cancel" v-bind="cancelProps" />
				</slot>
				<slot name="confirm">
					<DButton v-if="confirm !== false" v-bind="confirmProps" />
				</slot>
			</slot>
		</template>
	</DModal>
</template>
